import { Search, CalendarCheck, MapPin, Wrench } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const STEPS = [
  {
    num: "01",
    icon: Search,
    title: "Describe Your Issue",
    body: "Type your symptoms or select a service. Our smart diagnosis system suggests the right fix instantly.",
    darkColor: "#00F0FF",
    lightColor: "#0D9488",
  },
  {
    num: "02",
    icon: CalendarCheck,
    title: "Book & Confirm",
    body: "Choose your date, time and location. Get transparent pricing before you commit — no surprises.",
    darkColor: "#0055FF",
    lightColor: "#1E40AF",
  },
  {
    num: "03",
    icon: MapPin,
    title: "Track Your Mechanic",
    body: "Real-time GPS tracking just like a rideshare app. See your mechanic's ETA on a live map.",
    darkColor: "#00F0FF",
    lightColor: "#F97316",
  },
  {
    num: "04",
    icon: Wrench,
    title: "Fixed at Your Door",
    body: "Qualified technician arrives with the right parts. Pay only when the job is done to your satisfaction.",
    darkColor: "#0055FF",
    lightColor: "#0D9488",
  },
];

export default function HowItWorks() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <section id="how-it-works" className="py-20 sm:py-24 px-5 sm:px-6 md:px-12 lg:px-20 relative overflow-hidden">
      {/* Background accents */}
      <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${isDark ? "via-[#00F0FF]/20" : "via-brand-blue/10"} to-transparent`} />
      <div className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${isDark ? "via-[#0055FF]/20" : "via-brand-teal/10"} to-transparent`} />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className={`text-xs uppercase tracking-[0.2em] mb-3 font-mono font-semibold ${isDark ? "text-[#00F0FF]" : "text-brand-teal"}`}>
            The process
          </p>
          <h2 className={`font-display font-bold text-4xl lg:text-5xl tracking-tighter ${isDark ? "text-white" : "text-slate-900"}`}>
            How It Works
          </h2>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line — desktop */}
          <div className="hidden lg:block absolute top-10 left-[12.5%] right-[12.5%] h-px">
            <div className={`w-full h-full bg-gradient-to-r ${isDark ? "from-[#00F0FF]/30 via-[#0055FF]/30 to-[#00F0FF]/30" : "from-brand-teal/20 via-brand-blue/20 to-brand-orange/20"}`} />
            {/* Animated dash */}
            <svg className="absolute inset-0 w-full h-4 -top-1.5" viewBox="0 0 800 4" preserveAspectRatio="none">
              <line
                x1="0" y1="2" x2="800" y2="2"
                stroke={isDark ? "#00F0FF" : "#1E40AF"}
                strokeWidth="1.5"
                strokeOpacity="0.4"
                className="route-dash"
              />
            </svg>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              const color = isDark ? step.darkColor : step.lightColor;
              return (
                <div key={step.num} className="flex flex-col items-center text-center group">
                  {/* Icon circle */}
                  <div
                    className={`relative w-20 h-20 rounded-2xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:-translate-y-1 ${!isDark ? "shadow-md" : ""}`}
                    style={{
                      background: isDark
                        ? `rgba(${step.darkColor === "#00F0FF" ? "0,240,255" : "0,85,255"},0.08)`
                        : `${color}10`,
                      border: `1px solid ${color}30`,
                    }}
                  >
                    <Icon size={28} style={{ color }} />
                    {/* Step number */}
                    <span
                      className="absolute -top-2 -right-2 w-7 h-7 rounded-full text-[11px] font-mono font-bold flex items-center justify-center"
                      style={{
                        background: color,
                        color: "#ffffff",
                        boxShadow: isDark ? `0 0 12px ${color}80` : `0 2px 8px ${color}50`,
                      }}
                    >
                      {i + 1}
                    </span>
                  </div>

                  <h3 className={`font-display font-semibold text-lg mb-3 ${isDark ? "text-white" : "text-slate-900"}`}>
                    {step.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDark ? "text-[#8F9098]" : "text-slate-500"}`}>
                    {step.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
